import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router';

import { useTranslation } from 'react-i18next';
import { useSharedState, clearStorageValues } from '../hooks/SharedStateContext';
import { useTimer } from '../hooks/TimerContext';
import { usePlanner } from '../hooks/PlannerContext';

import MoreOptionsModal from './Modal/MoreOptionsModal';
import ModalGeneric from "./Modal/ModalGeneric";
import ConversationFeedbackModal from "./Modal/ConversationFeedbackModal";

import { useEndConversation } from '../hooks/endConversation';
import { getUrlParameter, isLiteMode, isActivityMode } from '../utilities/urlUtils';
import { formatTimeMMSS } from '../utilities/utils';
import { getEndConversationDestination } from '../utilities/endConversationNavigation';
import { trackFeedbackSurveySent, trackFeedbackSubmitted } from '../utilities/tracking';
import { signOutUser, getFirestoreDocId, updateFeedbackInFirestore } from '../utilities/firebase';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGamepad, faEllipsis, faBullhorn, faCircleInfo, faMap, faSpinner, faPhoneSlash, faClock } from '@fortawesome/free-solid-svg-icons';
import CustomPromptModal from "./Modal/CustomPromptModal";
import toast, { toastConfig } from 'react-simple-toasts';
import 'react-simple-toasts/dist/theme/dark.css';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

const translations = {
  en: {
    noticeBold: "Experience a very limited version of Madame AI!",
    noticeRegular: "Your audio data is kept for training purposes. See our ",
    privacyLinkText: "privacy policy",
    missingFeaturesTooltip: {
      title: 'Missing Features:',
      list: [
        'Slower interactions',
        'Only one dynamic activity',
        'No placement test or analysis',
        'No memory',
        'No analysis or feedback from your interactions',
      ],
    },
  },
  fr: {
    noticeBold: "Découvrez une version très limitée de Madame AI!",
    noticeRegular: "Vos données audio sont conservées anonymement. Consultez notre ",
    privacyLinkText: "politique de confidentialité",
    missingFeaturesTooltip: {
      title: 'Fonctionnalités manquantes :',
      list: [
        'Interactions plus lentes',
        'Une seule activité dynamique',
        'Aucun test de placement ou analyse',
        'Aucune mémoire',
        'Aucune analyse ou retour sur vos interactions',
      ],
    },
  },
}

export default function Header() {
  const navigate = useNavigate();
  const { sharedState, setSharedState, handleActivities, updateSelectedActivity } = useSharedState();

  // Always call the hook - it returns null in lite mode
  const plannerContext = usePlanner();
  
  const {
    showPlannerModal,
    plannerGoalForEdit,
    isPlannerLoading,
    isPlannerPending,
    hasPlannerNotification,
    handlePlannerSubmit,
    handlePlannerModalClose
  } = plannerContext || {};


  // Show loading state if either loading (manual creation) or pending (from onboarding)
  const showPlannerLoading = isPlannerLoading || isPlannerPending;

  const [isModalOpen, setIsModalOpen] = useState(false); // Modal: Settings
  const [isModalTestNeededOpened, setIsModalTestNeededOpened] = useState(false); // Modal: User needs to start a test
  const [isModalTimerOpen, setIsModalTimerOpen] = useState(false); // Modal: Test completed, go view results
  const [showFeedbackModal, setShowFeedbackModal] = useState(false); // Modal: feedback
  const [showConversationFeedbackModal, setShowConversationFeedbackModal] = useState(false); // Modal: conversation end feedback
  const [isTimerMenuOpen, setIsTimerMenuOpen] = useState(false);
  const [hasOptionsNotification, setHasOptionsNotification] = useState(() => {
    // Show notification if user hasn't seen the new speech speed feature
    return !localStorage.getItem('seenSpeedControlFeature');
  });

  const [currentDisplayTextLanguage, setCurrentDisplayTextLanguage] = useState('en'); // For the header's own text
  const lastConversationIdRef = useRef(null);
  const lastActivityRef = useRef(null);
  const timerMenuRef = useRef(null);

  const { remainingTime, isTimerFinished, stopActivityTimer, resetTimerFinishedStatus } = useTimer();
  const { endConversation } = useEndConversation();
  const { t } = useTranslation();

  toastConfig({
    theme: 'dark',
  });

  useEffect(() => {
    // Function to determine the language
    const determineLanguage = () => {
      // 1. Check for URL query parameter (highest priority)
      const langFromQuery = getUrlParameter('lng');

      if (langFromQuery && translations[langFromQuery.toLowerCase()]) {
        return langFromQuery.toLowerCase();
      }

      // 2. Check localStorage for user's explicitly selected language (second priority)
      const storedLang = localStorage.getItem('userLanguage');
      if (storedLang && translations[storedLang.toLowerCase()]) {
        console.log("determineLanguage: Using stored language from localStorage: " + storedLang.toLowerCase());
        return storedLang.toLowerCase();
      }

      // 3. Check browser language (third priority)
      const browserLang = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
      const primaryBrowserLang = browserLang.split('-')[0]; // Get 'fr' from 'fr-CA'

      if (translations[primaryBrowserLang]) {
        console.log("Header: Using browser language: " + primaryBrowserLang);
        return primaryBrowserLang;
      }

      return 'en'; // Default to English
    };

    const determinedLang = determineLanguage();
    setCurrentDisplayTextLanguage(determinedLang); // For the header's own display text

    // Update userLanguage in SharedStateContext only if it's different or not yet set
    // But NEVER override if user has explicitly selected a language (localStorage exists)
    const hasExplicitLanguageChoice = localStorage.getItem('userLanguage') !== null;
    
    if (!hasExplicitLanguageChoice && sharedState.userLanguage !== determinedLang) {
      console.log(`Header: Setting userLanguage in SharedStateContext to: ${determinedLang} (no explicit choice)`);
      setSharedState(prevState => ({
        ...prevState,
        userLanguage: determinedLang
      }));
    } else if (hasExplicitLanguageChoice) {
      console.log(`Header: Preserving user's explicit language choice: ${localStorage.getItem('userLanguage')}`);
    }

  }, [setSharedState, sharedState.userLanguage]); // Add dependencies

  // Open placement test modal when shouldPromptPlacementTest is true
  useEffect(() => {
    if (sharedState.shouldPromptPlacementTest && !isModalTestNeededOpened) {
      setIsModalTestNeededOpened(true);
    }
  }, [sharedState.shouldPromptPlacementTest, isModalTestNeededOpened]);

  /** TIMER END MODAL */

  // Effect to react to the timer finishing
  useEffect(() => {
    if (isTimerFinished) {
      console.log("Timer finished, ending conversation and showing modal.");
      // Reset the flag immediately after taking action
      // This prevents the effect from running again with isTimerFinished === true
      resetTimerFinishedStatus();
      setIsModalTimerOpen(true); // Show the timer end modal
      switchToActivities();
      // No need to call stopActivityTimer() here, context already stopped the interval
    }
  }, [isTimerFinished, endConversation, resetTimerFinishedStatus]);

  useEffect(() => {
    if (remainingTime <= 0) {
      setIsTimerMenuOpen(false);
    }
  }, [remainingTime]);

  useEffect(() => {
    if (!isTimerMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (timerMenuRef.current && !timerMenuRef.current.contains(event.target)) {
        setIsTimerMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsTimerMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTimerMenuOpen]);

  const closeTimerModal = (redirect) => {
    if (redirect) {
      window.open('https://francoflex.com/dashboard', '')
    }
    setIsModalTimerOpen(false);
  };

  /** SETTINGS MODAL */
  const openModal = () => {
    // Don't stack the settings modal on top of another open modal.
    if (showFeedbackModal || showConversationFeedbackModal || showPlannerModal) {
      return;
    }
    setIsModalOpen(true);
    // Clear the notification badge when user opens options
    if (hasOptionsNotification) {
      localStorage.setItem('seenSpeedControlFeature', 'true');
      setHasOptionsNotification(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  /** Start Placement Test */
  const closePlacemenTestModal = (startTest) => {
    if (startTest) {
      stopActivityTimer();
      endConversation();

      // Mark placement test as started in this session to prevent re-prompting (even if /end times out)
      sessionStorage.setItem('placementTestStartedThisSession', 'true');
      // sessionStorage dies with the tab, so also record the attempt durably. A test that
      // breaks mid-way writes no completion record, and without this the learner gets
      // re-prompted every new session forever (MAD-788).
      localStorage.setItem('placementTestAttemptedDate', Date.now().toString());
      console.log('Placement test started - marked in session to prevent re-prompting');

      setSharedState(prevState => ({
        ...prevState,
        firstUserInput: true,
        shouldPromptPlacementTest: false, // <-- Ensure this is set
      }));
      updateSelectedActivity('placement-test', true);
    } else {
      // User skipped the placement test - track skip date to prevent re-prompting for 1 week
      localStorage.setItem('placementTestSkippedDate', Date.now().toString());
      console.log('User skipped placement test, will not prompt again for 1 week');
      setSharedState(prevState => ({
        ...prevState,
        shouldPromptPlacementTest: false, // <-- Ensure this is set
      }));
    }
    setIsModalTestNeededOpened(false);
  };

  /** NAVIGATION */

  // Helper function to detect if we should confirm before ending current session
  const shouldConfirmNavigation = () => {
    // Confirm if:
    // 1. An activity is selected AND conversation view is active, OR
    // 2. There's an active conversation with chat history
    return (
      (sharedState.selectedActivity && sharedState.currentView === 'conversation') ||
      (sharedState.chatHistory && sharedState.chatHistory.length > 0)
    );
  };

  const switchToActivities = () => {
    if (shouldConfirmNavigation()) {
      if (!window.confirm(t('confirmEndActivity'))) {
        return;
      }
    }
    stopActivityTimer();
    const hasConversation = sharedState.chatHistory?.length > 0;
    const shouldPromptFeedback = sharedState.chatHistory?.length > 2;
    if (shouldPromptFeedback) {
      lastConversationIdRef.current = getFirestoreDocId();
      lastActivityRef.current = sharedState.selectedActivity;
    }
    if (hasConversation) {
      endConversation();          // fire immediately — /end is guaranteed
    }
    if (shouldPromptFeedback) {
      setShowConversationFeedbackModal(true);
    }
    const destination = getEndConversationDestination({ selectedActivity: sharedState.selectedActivity });
    if (destination === 'planner') {
      navigate('/objective');
    } else {
      handleActivities();
    }
  };

  // End conversation in lite/activity mode: no redirect to activity list
  const endConversationOnly = () => {
    const isOnboarding = sharedState.selectedActivity === 'onboarding-intro';
    const chatLength = sharedState.chatHistory?.length || 0;

    if (isOnboarding && chatLength < 10) {
      if (!window.confirm(t('confirmEndOnboarding'))) {
        return;
      }
    } else if (shouldConfirmNavigation()) {
      if (!window.confirm(t('confirmEndActivity'))) {
        return;
      }
    }
    stopActivityTimer();
    // In activity mode, trigger the test completed callback (handles /end + results)
    // Skip feedback modal — activity pages have their own completion flow
    if (sharedState.onTestCompleted) {
      sharedState.onTestCompleted();
      return;
    }
    const hasConversation = sharedState.chatHistory?.length > 0;
    const shouldPromptFeedback = sharedState.chatHistory?.length > 2;
    if (shouldPromptFeedback) {
      lastConversationIdRef.current = getFirestoreDocId();
      lastActivityRef.current = sharedState.selectedActivity;
    }
    if (hasConversation) {
      endConversation();
    }
    if (shouldPromptFeedback) {
      setShowConversationFeedbackModal(true);
    }
  };

  const handleConversationFeedbackSubmit = (feedbackData) => {
    setShowConversationFeedbackModal(false);
    if (lastConversationIdRef.current) {
      updateFeedbackInFirestore(lastConversationIdRef.current, feedbackData);
      trackFeedbackSubmitted(lastConversationIdRef.current, lastActivityRef.current, feedbackData);
    }
    lastConversationIdRef.current = null;
    lastActivityRef.current = null;
  };

  const handleConversationFeedbackDismiss = () => {
    setShowConversationFeedbackModal(false);
    lastConversationIdRef.current = null;
    lastActivityRef.current = null;
  };

  const handlePlannerClickWithConfirmation = () => {
    if (shouldConfirmNavigation()) {
      if (!window.confirm(t('confirmEndActivity'))) {
        return;
      }
    }
    stopActivityTimer();
    const shouldPromptFeedback = sharedState.chatHistory?.length > 2;
    if (shouldPromptFeedback) {
      lastConversationIdRef.current = getFirestoreDocId();
      lastActivityRef.current = sharedState.selectedActivity;
    }
    endConversation();
    if (shouldPromptFeedback) {
      setShowConversationFeedbackModal(true);
    }
    navigate('/objective');
  };

  const newConversation = () => {
    if (shouldConfirmNavigation()) {
      if (!window.confirm(t('confirmEndActivity'))) {
        return;
      }
    }
    stopActivityTimer();
    const shouldPromptFeedback = sharedState.chatHistory?.length > 2;
    if (shouldPromptFeedback) {
      lastConversationIdRef.current = getFirestoreDocId();
      lastActivityRef.current = sharedState.selectedActivity;
    }
    endConversation();
    if (shouldPromptFeedback) {
      setShowConversationFeedbackModal(true);
    }
    closeModal();
  }

  const resetAcknowledgement = () => {
    if (window.confirm(t('confirmReset'))) {
      // Clear localStorage values
      clearStorageValues();

      // Reset sharedState values to prevent useEffect from re-saving them
      setSharedState(prevState => ({
        ...prevState,
        userAge: null,
        userLanguage: null,
        userLevel: null,
        languageVariant: null,
        isRealtime: false,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        ttsSpeed: 1.0,
        preferredPronoun: 'both',
        chatHistory: [],
        selectedActivity: null,
      }));

      // Sign out the user to get fresh anonymous auth (signOutUser handles reload)
      signOutUser();
    }
    closeModal();
  };

  // Extract survey details (can be fetched or stored constants)
  const posthogSurveyDetails = {
    id: "0195bff6-4adb-0000-1512-7f784613afe4",
    question: t('feedback_question'),
    description: t('feedback_description'),
    // Extract question ID if needed for PostHog event properties
    questionId: "5e9a4aba-ccb3-41e3-ae1c-4defcef5d666"
  };

  const handleFeedbackClick = () => {
    // Never open the product-feedback modal on top of another modal (e.g. the
    // conversation-feedback prompt shown at the end of a conversation, or settings).
    if (isModalOpen || showConversationFeedbackModal || showPlannerModal) {
      return;
    }
    setShowFeedbackModal(true);
  };
  // This function handles the submission *from* the modal
  const handleFeedbackSubmit = (feedbackText) => {
    // Use the centralized tracking function
    const success = trackFeedbackSurveySent(posthogSurveyDetails.id, feedbackText)

    if (success) {
      toast('Thank you for your feedback!') // User confirmation
      // Modal closes itself via its own onClose call in handleSave
    } else if (!feedbackText.trim()) {
      toast('Please enter some feedback before submitting.')
    } else {
      // This case now specifically means PostHog was unavailable in tracking.js
      toast('Could not submit feedback at this time.') // Error feedback
    }
  };

  // Extract planner details (can be fetched or stored constants)
  const plannerSurveyDetails = {
    question: t('planner_question'),
    description: t('planner_description')
  };

  const currentText = translations[currentDisplayTextLanguage] || translations.en;
  const isReducedMode = isLiteMode() || isActivityMode();
  const formattedRemainingTime = formatTimeMMSS(remainingTime);

  // Modal governance: while any modal is open, suppress the header's feedback /
  // more-options buttons so a second modal can't be stacked on top or receive a
  // click-through (fixes the onboarding double-modal bug).
  const anyModalOpen =
    isModalOpen ||
    showFeedbackModal ||
    showConversationFeedbackModal ||
    showPlannerModal ||
    isModalTimerOpen ||
    isModalTestNeededOpened;

  return (
    <header className="main-header">
      <div className="header-options">
        <div className="inline-block">
          {isLiteMode() ? (
            <p className="privacy-notice">
              <span className="bold">{currentText.noticeBold}</span>
              <div className="inline-block align-middle">
                <div className="tooltip-group">
                  <FontAwesomeIcon icon={faCircleInfo} className="text-blue-500 cursor-pointer" />
                  <div className="tooltip-content">
                    <div className="font-bold mb-1">{currentText.missingFeaturesTooltip.title}</div>
                    <ul className="list-disc pl-4">
                      {currentText.missingFeaturesTooltip.list.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              <br />
              {currentText.noticeRegular}
              <a href="https://conversaflex.com/privacy" target="_blank" rel="noopener noreferrer">
                {currentText.privacyLinkText}
              </a>

            </p>
          ) : (
            <>
              {/* {sharedState.selectedActivity && (
                <h3>{sharedState.selectedActivity}</h3>
              )} */}
            </>
          )}
        </div>
        {/* Hide activities, planner buttons in lite/activity mode */}
        {!isReducedMode && (
          <>
            <div
              className="button activities-button"
              onClick={switchToActivities}
              data-tooltip-id="header-activities-tooltip"
              data-tooltip-content={t('tooltip_activities')}
              aria-label={t('tooltip_activities')}
            >
              <FontAwesomeIcon icon={faGamepad} />
            </div>
            <div
              className={`button activities-button planner-button ${showPlannerLoading ? 'loading' : ''} ${hasPlannerNotification ? 'has-notification' : ''}`}
              onClick={handlePlannerClickWithConfirmation}
              data-tooltip-id="header-planner-tooltip"
              data-tooltip-content={showPlannerLoading ? t('plannerBeingCreated') : t('tooltip_planner')}
              aria-label={showPlannerLoading ? t('plannerBeingCreated') : t('tooltip_planner')}
              style={{
                opacity: showPlannerLoading ? 0.6 : 1,
                cursor: showPlannerLoading ? 'not-allowed' : 'pointer',
                position: 'relative'
              }}
            >
              <FontAwesomeIcon
                icon={showPlannerLoading ? faSpinner : faMap}
                spin={showPlannerLoading}
              />
              {hasPlannerNotification && !showPlannerLoading && (
                <div className="notification-badge"></div>
              )}
            </div>
            <Tooltip id="header-activities-tooltip" place="bottom" />
            <Tooltip id="header-planner-tooltip" place="bottom" />
          </>
        )}

        {/* Feedback button - hidden while another modal is open to prevent stacking/click-through */}
        {!anyModalOpen && (
          <div
            className="button feedback-button"
            onClick={handleFeedbackClick}
            data-tooltip-id="header-feedback-tooltip"
            data-tooltip-content={t('tooltip_feedback')}
            aria-label={t('tooltip_feedback')}
          >
            <FontAwesomeIcon icon={faBullhorn} />
          </div>
        )}

        {/* Timer - compact on narrow screens to prevent header overflow */}
        {remainingTime > 0 && (
          <div className={`activity-timer-wrapper ${isTimerMenuOpen ? 'open' : ''}`} ref={timerMenuRef}>
            <h3 className="activity-timer activity-timer-inline">{t('time_remaining')}: {formattedRemainingTime}</h3>
            <button
              className="button activity-timer-toggle"
              type="button"
              onClick={() => setIsTimerMenuOpen(prev => !prev)}
              aria-expanded={isTimerMenuOpen}
              aria-label={`${t('time_remaining')}: ${formattedRemainingTime}`}
              data-tooltip-id="header-timer-tooltip"
              data-tooltip-content={`${t('time_remaining')}: ${formattedRemainingTime}`}
            >
              <FontAwesomeIcon icon={faClock} />
              <span>{formattedRemainingTime}</span>
            </button>
            <div className="activity-timer-menu" role="status" aria-live="polite">
              <span className="activity-timer-label">{t('time_remaining')}</span>
              <strong>{formattedRemainingTime}</strong>
            </div>
            <Tooltip id="header-timer-tooltip" place="bottom" />
          </div>
        )}

        {/* More options button - visible except in lite mode, and hidden while a modal is open */}
        {!isLiteMode() && !anyModalOpen && (
          <div
            className={`button more-button ${hasOptionsNotification ? 'has-notification' : ''}`}
            onClick={openModal}
            data-tooltip-id="header-more-tooltip"
            data-tooltip-content={t('tooltip_more')}
            aria-label={t('tooltip_more')}
            style={{ position: 'relative' }}
          >
            <FontAwesomeIcon icon={faEllipsis} />
            {hasOptionsNotification && (
              <div className="notification-badge"></div>
            )}
          </div>
        )}

        {/* WCAG AAA compliant tooltips */}
        <Tooltip id="header-feedback-tooltip" place="bottom" />
        <Tooltip id="header-more-tooltip" place="bottom" />
      </div>

      {/* End Conversation Button - positioned on the right, only visible during active conversation */}
      {sharedState.currentView === 'conversation' && sharedState.firstUserInput && (
        <div className="header-right">
          <div
            className="button end-conversation-button"
            onClick={isReducedMode ? endConversationOnly : switchToActivities}
            data-tooltip-id="header-end-tooltip"
            data-tooltip-content={t('end_conversation_action', 'End conversation')}
            aria-label={t('end_conversation_action', 'End conversation')}
          >
            <FontAwesomeIcon icon={faPhoneSlash} />
            <span className="end-button-label">{t('end_label', 'End')}</span>
          </div>
          <Tooltip id="header-end-tooltip" place="bottom" />
        </div>
      )}

      <MoreOptionsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onNewConversation={newConversation}
        onEndConversation={endConversation}
        onResetAcknowledgement={resetAcknowledgement}
      />

      <ConversationFeedbackModal
        isOpen={showConversationFeedbackModal}
        onClose={handleConversationFeedbackDismiss}
        onSubmit={handleConversationFeedbackSubmit}
        onSkip={handleConversationFeedbackDismiss}
      />

      <ModalGeneric
        title={t('placement_test_title')}
        isOpen={isModalTimerOpen}
        onClose={() => closeTimerModal(false)}
      >
        <p>{t('placement_test_completed')}</p>
        <div className="modal-footer">
          <button onClick={() => closeTimerModal(true)} className="modal-button">Voir le résultat</button>
        </div>
      </ModalGeneric>

      <ModalGeneric
        title={t('placement_test_needed_title')}
        isOpen={isModalTestNeededOpened}
        onClose={() => closePlacemenTestModal(false)}
      >
        <p>{t('placement_test_needed_body')}</p>
        <p>{t('placement_test_duration_note', 'This usually takes about 15 minutes.')}</p>
        <div className="modal-footer">
          <button onClick={() => closePlacemenTestModal(false)} className="modal-button secondary">{t('remind_me_later', 'Remind me later')}</button>
          <button onClick={() => closePlacemenTestModal(true)} className="modal-button">{t('commencer')}</button>
        </div>
      </ModalGeneric>

      <CustomPromptModal
        // Never render on top of the conversation-feedback modal (mutual exclusion)
        isOpen={showFeedbackModal && !showConversationFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        title={posthogSurveyDetails.question} // Use PostHog question as title
        placeholder={posthogSurveyDetails.description} // Use PostHog description as placeholder
        submitButtonText={t('submit_feedback')} // Customize button text
      />
      
      {plannerContext && (
        <CustomPromptModal
          isOpen={showPlannerModal}
          onClose={handlePlannerModalClose}
          onSubmit={handlePlannerSubmit}
          title={plannerSurveyDetails.question}
          placeholder={plannerSurveyDetails.description}
          submitButtonText={t('submit_objective')}
          defaultValue={plannerGoalForEdit} // Pre-fill with current goal when editing
        />
      )}

    </header>
  );
};
